'use client';
import React, { useEffect, useRef, useState } from 'react';

const FEED_ITEMS = [
  { id: 1, user: 'Priya N.', action: 'posted a', target: 'Lucky Escape 🍀', time: '2s ago', color: '#3DFFC4', detail: 'Missed my flight — next one had an empty upgrade seat' },
  { id: 2, user: 'Marcus W.', action: 'logged a', target: 'Serendipity ✨', time: '14s ago', color: '#B4A0FF', detail: 'Ran into my dream employer at a coffee shop' },
  { id: 3, user: 'Yuki T.', action: 'shared a', target: 'Lucky Find 🎯', time: '31s ago', color: '#3DFFC4', detail: 'Found $200 in an old jacket pocket' },
  { id: 4, user: 'Aisha O.', action: 'reported a', target: 'Near Miss 🌀', time: '1m ago', color: '#B4A0FF', detail: 'Almost signed a bad contract — typo saved me' },
  { id: 5, user: 'Dmitri V.', action: 'posted a', target: 'Windfall 💸', time: '2m ago', color: '#3DFFC4', detail: 'Stock I forgot I owned tripled overnight' },
  { id: 6, user: 'Leila H.', action: 'logged a', target: 'Lucky Escape 🍀', time: '3m ago', color: '#B4A0FF', detail: 'Took a detour — avoided a 3-hour traffic jam' },
  { id: 7, user: 'Carlos M.', action: 'shared a', target: 'Serendipity ✨', time: '4m ago', color: '#3DFFC4', detail: 'Sat next to a publisher on a 6-hour flight' },
  { id: 8, user: 'Nneka O.', action: 'reported a', target: 'Lucky Find 🎯', time: '5m ago', color: '#B4A0FF', detail: 'Last ticket to sold-out show — bought it by accident' },
];

const LUCK_TYPES = [
  { type: 'Lucky Escape', count: 3241, color: '#3DFFC4' },
  { type: 'Serendipity', count: 2187, color: '#B4A0FF' },
  { type: 'Windfall', count: 1893, color: '#3DFFC4' },
];

const TOP_VISITORS = [
  { name: 'Priya', incidents: 14, img: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?w=40&h=40&fit=crop&crop=face' },
  { name: 'Marcus', incidents: 11, img: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face' },
  { name: 'Yuki', incidents: 9, img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face' },
  { name: 'Aisha', incidents: 8, img: 'https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=40&h=40&fit=crop&crop=face' },
  { name: 'Dmitri', incidents: 7, img: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face' },
];

export default function DashboardPreview() {
  const cardRef = useRef<HTMLDivElement>(null);
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [feedItems, setFeedItems] = useState(FEED_ITEMS.slice(0, 4));
  const feedIndexRef = useRef(0);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!cardRef.current) return;
      const rect = cardRef.current.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = (e.clientX - cx) / (rect.width / 2);
      const dy = (e.clientY - cy) / (rect.height / 2);
      setTilt({ x: dy * -6, y: dx * 6 });
    };
    const handleMouseLeave = () => setTilt({ x: 0, y: 0 });
    const el = cardRef.current;
    if (el) {
      el.addEventListener('mousemove', handleMouseMove);
      el.addEventListener('mouseleave', handleMouseLeave);
    }
    return () => {
      if (el) {
        el.removeEventListener('mousemove', handleMouseMove);
        el.removeEventListener('mouseleave', handleMouseLeave);
      }
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      feedIndexRef.current = (feedIndexRef.current + 1) % FEED_ITEMS.length;
      setFeedItems((prev) => {
        const next = [...prev.slice(1)];
        next.push(FEED_ITEMS[(feedIndexRef.current + 3) % FEED_ITEMS.length]);
        return next;
      });
    }, 2200);
    return () => clearInterval(interval);
  }, []);

  return (
    <div
      ref={cardRef}
      className="tilt-card glass rounded-2xl p-4 shadow-glass-lg w-full max-w-[520px] cursor-default select-none"
      style={{
        transform: `perspective(1000px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.12)',
      }}
    >
      {/* Dashboard header */}
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-white/[0.08]">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <span className="w-2 h-2 rounded-full bg-mint inline-block" />
            <span className="text-xs font-display font-700 text-mint tracking-wide uppercase">LuckCount Live Feed</span>
          </div>
          <p className="text-white/40 text-xs font-body">Global · {(8241).toLocaleString()} unique visitors today</p>
        </div>
        <div className="flex items-center gap-1.5">
          {['#escape', '#windfall', '#serendipity'].map((ch) => (
            <span key={ch} className="badge-lilac text-[10px] px-2 py-0.5 rounded-full font-mono">{ch}</span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-[1fr_160px] gap-3">
        {/* Left: Live Incident Feed */}
        <div>
          <p className="text-[10px] font-display font-600 text-white/30 uppercase tracking-widest mb-2">Live Incidents</p>
          <div className="space-y-2 overflow-hidden" style={{ height: '180px' }}>
            {feedItems.map((item, i) => (
              <div
                key={`${item.id}-${i}`}
                className="flex items-start gap-2 p-2 rounded-lg transition-all duration-700"
                style={{ background: 'rgba(255,255,255,0.04)', opacity: 1 - i * 0.12 }}
              >
                <div
                  className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                  style={{ background: item.color, boxShadow: `0 0 6px ${item.color}` }}
                />
                <p className="text-xs font-body leading-snug">
                  <span className="text-iwhite font-medium">{item.user}</span>
                  <span className="text-white/50"> {item.action} </span>
                  <span style={{ color: item.color }}>{item.target}</span>
                  <span className="text-white/30 ml-1">{item.time}</span>
                </p>
              </div>
            ))}
          </div>

          {/* Top contributors */}
          <div className="mt-3 pt-3 border-t border-white/[0.06]">
            <p className="text-[10px] font-display font-600 text-white/30 uppercase tracking-widest mb-2">Top Reporters</p>
            <div className="flex items-center gap-1.5">
              {TOP_VISITORS.map((m) => (
                <div key={m.name} className="relative">
                  <div className="w-7 h-7 rounded-full overflow-hidden border border-white/20">
                    <img src={m.img} alt={m.name} className="w-full h-full object-cover" />
                  </div>
                  <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-[#0D0F1C] flex items-center justify-center">
                    <span className="text-[7px] font-mono text-mint">{m.incidents}</span>
                  </span>
                </div>
              ))}
              <span className="text-[10px] text-white/30 ml-1 font-body">+2.4k reporters</span>
            </div>
          </div>
        </div>

        {/* Right: Luck type breakdown */}
        <div>
          <p className="text-[10px] font-display font-600 text-white/30 uppercase tracking-widest mb-2">By Type</p>
          <div className="space-y-2">
            {LUCK_TYPES.map((lt) => (
              <div
                key={lt.type}
                className="p-2.5 rounded-xl"
                style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.07)' }}
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: lt.color }} />
                  <span className="text-[10px] font-display font-600 text-white/80 leading-tight">{lt.type}</span>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex-1 h-1 rounded-full bg-white/10 mr-2">
                    <div className="h-1 rounded-full" style={{ background: lt.color, width: `${(lt.count / 3241) * 100}%` }} />
                  </div>
                  <span className="text-[10px] font-mono" style={{ color: lt.color }}>{lt.count.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>

          {/* Recent highlight */}
          <div
            className="mt-3 p-2.5 rounded-xl"
            style={{ background: 'rgba(180,160,255,0.08)', border: '1px solid rgba(180,160,255,0.2)' }}
          >
            <p className="text-[10px] font-display font-600 text-lilac mb-1.5">Trending Tags</p>
            {['#travel-luck', '#career-luck', '#money-luck'].map((r) => (
              <div key={r} className="flex items-center gap-1.5 mb-1">
                <span className="w-1 h-1 rounded-full bg-lilac/60" />
                <span className="text-[9px] text-white/50 font-body truncate">{r}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}